import { ImageView, MovieClip, View } from '@play-co/timestep-core/ui';
import { Container, clamp } from '../../../../odie';
import { getMatch2AssetPath } from '../../../util/assets';
import { config } from '../../../defs/config';
import { ColorId } from '../../../defs/block';
import gsap from 'gsap';
import { AssetDescription } from '@play-co/timestep-core';

const manifest = () => ({
  red: {
    back: 'soda/block.soda.base.red.png',
    front: 'soda/block.soda.overlay.red.png',
    fill: 'soda/block.soda.fill.red.png',
  },
  blue: {
    back: 'soda/block.soda.base.blue.png',
    front: 'soda/block.soda.overlay.blue.png',
    fill: 'soda/block.soda.fill.blue.png',
  },
  green: {
    back: 'soda/block.soda.base.green.png',
    front: 'soda/block.soda.overlay.green.png',
    fill: 'soda/block.soda.fill.green.png',
  },
  yellow: {
    back: 'soda/block.soda.base.yellow.png',
    front: 'soda/block.soda.overlay.yellow.png',
    fill: 'soda/block.soda.fill.yellow.png',
  },
  purple: {
    back: 'soda/block.soda.base.purple.png',
    front: 'soda/block.soda.overlay.purple.png',
    fill: 'soda/block.soda.fill.purple.png',
  },
  movieClip: {
    url: getMatch2AssetPath('blocks/soda/movieclip'),
    loadMethod: MovieClip.animationLoader,
  },
});

export type SodaColor = Exclude<ColorId, 'all'>;

type SodaBottleAnimationKey =
  | 'soda_master'
  | 'soda_blastoff'
  | 'soda_fill_FX_overlay'
  | 'placeholder_back'
  | 'placeholder_front';

/**
 * This is a view that represents the liquid inside the soda bottle.
 * It's a bit of a hack, meant to replace the liquid in the soda bottle animation.
 */
class SodaFill extends View {
  public mask: View;
  public liquid: ImageView;
  private _fillRatio = 1;

  constructor(color: SodaColor) {
    super({
      width: 66,
      height: 176,
    });

    const { fill } = manifest()[color];

    this.mask = new View({
      superview: this,
      width: this.style.width,
      height: this.style.height,
      centerOnOrigin: true,
      clip: true,
    });

    this.liquid = new ImageView({
      image: getMatch2AssetPath(fill),
      superview: this.mask,
      width: this.style.width,
      height: this.style.height,
    });
  }

  public get fillRatio() {
    return this._fillRatio;
  }

  public set fillRatio(value: number) {
    this._fillRatio = clamp(value, 0, 1);
    const y = this.style.height - this._fillRatio * this.style.height;
    const h = this._fillRatio * this.style.height;
    this.liquid.style.y = -y;
    this.mask.style.y = y;
    this.mask.style.height = h;
  }
}

class SodaAnimation extends MovieClip {
  public fill: SodaFill;

  constructor(color: SodaColor) {
    super({
      url: getMatch2AssetPath(manifest().movieClip.url),
      blockEvents: true,
      anchorY: 100,
    });

    // Increase speed a little bit to not look like a bad framerate
    this.speed = 1.5;

    const { front, back } = manifest()[color];

    const backContainer = new View({
      width: 66,
      height: 176,
    });

    new ImageView({
      superview: backContainer,
      image: getMatch2AssetPath(back),
      centerOnOrigin: true,
      width: 66,
      height: 176,
    });

    this.addViewSubstitution('back', backContainer);

    this.addViewSubstitution(
      'front',
      new ImageView({
        image: getMatch2AssetPath(front),
        width: 66,
        height: 176,
        offsetX: -66 * 0.5,
        offsetY: -176 * 0.5 - 10,
      }),
    );

    // Create the dynamic soda fill, that will scale up with fillRatio.
    this.fill = new SodaFill(color);

    // Add the fill to the back container, to prevent the idle animation flickering while playing in loop
    // This should be fixed in the animation instead.
    this.fill.updateOpts({
      superview: backContainer,
    });

    // Replace all "fill" sprites in the animation by the dynamic soda fill view. This also should be sorted in the animation data,
    // so there is no need for multiple frames ad the bottle fills up, only a single placeholder
    for (let i = 0; i <= 13; i++) {
      this.addViewSubstitution(`fill-${i}`, this.fill);
    }
  }

  public async animate(key: SodaBottleAnimationKey, loop?: boolean) {
    if (loop) {
      this.play(key, null, loop);
      return;
    }
    await this.playAsync(key);
  }
}

export default class SodaBottle extends Container {
  private _base: SodaAnimation;
  private _overlay: SodaAnimation;
  private _fillRatio!: number;

  public static assets(): (string | AssetDescription<any>)[] {
    return [
      ...Object.values(manifest().red),
      ...Object.values(manifest().blue),
      ...Object.values(manifest().green),
      ...Object.values(manifest().yellow),
      ...Object.values(manifest().purple),
      manifest().movieClip,
    ];
  }

  constructor(color: SodaColor) {
    super();
    const tileSize = config.tile.size;
    this._base = this._createAnimationLayer(color);
    this._overlay = this._createAnimationLayer(color);
    this._base.animate('soda_master', true);
    this.width = tileSize;
    this.height = tileSize * 2;
    this.fillRatio = 0;
    this.scaleX = this.scaleY = 0.85;
  }

  private _createAnimationLayer(color: SodaColor) {
    const layer = new SodaAnimation(color);
    this.view.addSubview(layer);
    return layer;
  }

  public async playFillFx() {
    if (this._overlay.isPlaying) return;
    await Promise.all([
      gsap
        .timeline()
        .set(this._base.style, { scaleX: 1, scaleY: 1, overwrite: true })
        .to(this._base.style, {
          duration: 0.2,
          scaleX: 1.3,
          scaleY: 0.8,
          ease: 'none',
        })
        .to(this._base.style, {
          duration: 0.1,
          scaleX: 1,
          scaleY: 0.9,
          ease: 'none',
        })
        .to(this._base.style, {
          duration: 0.1,
          scaleX: 1,
          scaleY: 1,
          ease: 'none',
        }),
      this._overlay.animate('soda_fill_FX_overlay'),
    ]);
  }

  public async playLaunchFx() {
    await this._base.animate('soda_blastoff');
  }

  public get fillRatio() {
    return this._fillRatio;
  }

  public set fillRatio(value: number) {
    this._fillRatio = clamp(value, 0, 1);
    this._base.frame = Math.round(value * (this._base.frameCount - 1));
    this._base.fill.fillRatio = this._fillRatio;
  }
}
